import * as React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import TitleIcon from "@mui/icons-material/Title";

/**
 * TipTap-based rich text editor.
 * @param {string} value - HTML string (controlled)
 * @param {(html: string) => void} onChange
 * @param {number} [minHeight=200]
 * @param {boolean} [readOnly=false]
 */
export default function RichTextEditor({ value, onChange, minHeight = 200, readOnly = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const editor = useEditor({
    extensions: [
      // Disable StarterKit's built-in Underline so our explicit import doesn't duplicate it
      StarterKit.configure({ underline: false }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editable: !readOnly,
    // Sync initial content to React state so parent sees the real HTML on first render
    onCreate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
  });

  // Sync external value changes (e.g. loading a template)
  const prevValue = React.useRef(value);
  React.useEffect(() => {
    if (!editor || value === prevValue.current) return;
    prevValue.current = value;
    // Only update if content actually differs to avoid cursor jump
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || "", false);
    }
  }, [editor, value]);

  const ToolbarBtn = ({ title, active, onClick, children }) => (
    <Tooltip title={title} placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        color={active ? "primary" : "default"}
        sx={{ borderRadius: 1 }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        "&:focus-within": {
          borderColor: "primary.main",
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
        },
      }}
    >
      {!readOnly && (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.25,
              px: 1,
              py: 0.5,
              bgcolor: isDark ? "grey.900" : "grey.100",
            }}
          >
            <ToolbarBtn
              title="Heading"
              active={editor?.isActive("heading", { level: 3 })}
              onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <TitleIcon fontSize="small" />
            </ToolbarBtn>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarBtn
              title="Bold"
              active={editor?.isActive("bold")}
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              <FormatBoldIcon fontSize="small" />
            </ToolbarBtn>
            <ToolbarBtn
              title="Italic"
              active={editor?.isActive("italic")}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            >
              <FormatItalicIcon fontSize="small" />
            </ToolbarBtn>
            <ToolbarBtn
              title="Underline"
              active={editor?.isActive("underline")}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </ToolbarBtn>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarBtn
              title="Bullet list"
              active={editor?.isActive("bulletList")}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulletedIcon fontSize="small" />
            </ToolbarBtn>
            <ToolbarBtn
              title="Numbered list"
              active={editor?.isActive("orderedList")}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToolbarBtn>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarBtn
              title="Align left"
              active={editor?.isActive({ textAlign: "left" })}
              onClick={() => editor?.chain().focus().setTextAlign("left").run()}
            >
              <FormatAlignLeftIcon fontSize="small" />
            </ToolbarBtn>
            <ToolbarBtn
              title="Align center"
              active={editor?.isActive({ textAlign: "center" })}
              onClick={() => editor?.chain().focus().setTextAlign("center").run()}
            >
              <FormatAlignCenterIcon fontSize="small" />
            </ToolbarBtn>
            <ToolbarBtn
              title="Align right"
              active={editor?.isActive({ textAlign: "right" })}
              onClick={() => editor?.chain().focus().setTextAlign("right").run()}
            >
              <FormatAlignRightIcon fontSize="small" />
            </ToolbarBtn>
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          minHeight,
          px: 2,
          py: 1.5,
          "& .ProseMirror": {
            outline: "none",
            minHeight,
            fontSize: "0.95rem",
            lineHeight: 1.65,
            "& h1, & h2, & h3": { mt: 2, mb: 1 },
            "& p": { my: 0.75 },
            "& ul, & ol": { pl: 3 },
            "& [data-placeholder]::before": {
              content: "attr(data-placeholder)",
              color: "text.disabled",
              pointerEvents: "none",
              position: "absolute",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
}
